import { WhipError, errorMessage } from './errors';
import type { WhipConnectionState, WhipEventListener, WhipEventMap, WhipEventType } from './events';
import type { WhipLogger } from './logger';
import { MAX_VIDEO_SHORT_SIDE, MIN_VIDEO_SHORT_SIDE, enableAbsCaptureTime, videoSectionLines, videoShortSide } from './sdp';
import { buildIngestUrl, deleteResource, postOffer } from './whip-http';

/** Every option resolved to a value, so the session itself has no defaults to remember. */
export interface WhipSessionConfig {
	endpoint: string;
	stream: MediaStream;
	headers: Record<string, string>;
	absCaptureTime: boolean;
	maxVideoShortSide: number | null | undefined;
	maxBitrate: number | undefined;
	degradationPreference: RTCDegradationPreference | undefined;
	logger: WhipLogger;
	fetchImpl: typeof fetch | undefined;
}

/**
 * One WHIP session on one ingest endpoint.
 *
 * Built by {@link createWhipSession} and started by the caller, which is what leaves room to subscribe first — every
 * state this session ever reaches then arrives through `on('statechange')`, with no second event channel for the ones
 * that happen during negotiation.
 *
 * Holds a peer connection and, once the server has answered, the url of the resource representing the session. Those two
 * are what `close()` disposes of.
 */
export class WhipSession {
	#config: WhipSessionConfig;
	#pc: RTCPeerConnection | null = null;
	#resourceUrl: string | null = null;
	#debugHlsStreamId: string | null = null;
	#started = false;
	#closed = false;
	#listeners = new Map<WhipEventType, Set<WhipEventListener<WhipEventType>>>();

	constructor(config: WhipSessionConfig) {
		this.#config = config;
	}

	/**
	 * Where the connection is, read straight off the peer connection so there is no second copy to fall out of step.
	 *
	 * `new` before `start()`, `closed` after `close()`. `failed` is terminal — this stack has no ICE restart to offer.
	 */
	get state(): WhipConnectionState {
		if (this.#closed) return 'closed';
		return this.#pc?.connectionState ?? 'new';
	}

	/** The session resource, once the server has named one. `null` means `close()` has nothing to delete. */
	get resourceUrl(): string | null {
		return this.#resourceUrl;
	}

	/** The transcoder's id for the HLS output. A debugging affordance; see {@link import('./whip-http').WhipPostResult}. */
	get debugHlsStreamId(): string | null {
		return this.#debugHlsStreamId;
	}

	/** Subscribes to one event type. Returns the unsubscribe. */
	on<T extends WhipEventType>(type: T, listener: WhipEventListener<T>): () => void {
		const set = this.#listeners.get(type) ?? new Set();
		set.add(listener as WhipEventListener<WhipEventType>);
		this.#listeners.set(type, set);
		return () => set.delete(listener as WhipEventListener<WhipEventType>);
	}

	/**
	 * Negotiates the session: offer out, answer in. Resolves once the answer is applied.
	 *
	 * That is before ICE has finished, so `state` is usually still `connecting` on return and reaches `connected` through
	 * an event. Rejects with a {@link WhipError}; nothing is left running behind a rejection, including a server resource
	 * created just before the failure.
	 *
	 * A session publishes once. To change what is sent, close this one and create another.
	 */
	async start(): Promise<void> {
		if (this.#closed) throw new WhipError('closed', 'this session is closed; create another');
		if (this.#started) throw new WhipError('already-started', 'this session is already started; create another to stream again');
		this.#started = true;

		try {
			await this.#negotiate();
		} catch (error) {
			// Whatever got as far as existing — a peer connection, or a server resource created just before the answer was
			// refused — is given back here, so a rejection leaves nothing running and nothing holding a transcoder slot.
			await this.close();
			throw error;
		}
	}

	async #negotiate(): Promise<void> {
		const { logger } = this.#config;
		const [videoTrack, audioTrack] = this.#requireVideoAndAudioTracks();

		const pc = new RTCPeerConnection();
		this.#pc = pc;
		this.#watch(pc);

		// Video first, and via addTransceiver rather than addTrack, so `maxBitrate` can be set at creation: Chrome derives
		// its own bitrate ceiling from the frame size, and misjudges it badly for unusual aspect ratios.
		const videoTransceiver = pc.addTransceiver(videoTrack, {
			direction: 'sendonly',
			streams: [this.#config.stream],
			...(this.#config.maxBitrate ? { sendEncodings: [{ maxBitrate: this.#config.maxBitrate }] } : {}),
		});
		const audioTransceiver = pc.addTransceiver(audioTrack, { direction: 'sendonly', streams: [this.#config.stream] });

		if (this.#config.absCaptureTime) {
			const enabled = [videoTransceiver, audioTransceiver].filter((transceiver) => enableAbsCaptureTime(transceiver, logger)).length;
			logger.debug(`[whip] abs-capture-time requested on ${enabled} transceiver(s)`);
		}

		const offer = await this.#createOffer(pc);
		const url = buildIngestUrl(this.#config.endpoint, { maxVideoShortSide: this.#maxVideoShortSide(videoTrack) });

		logger.debug(`[whip] POST ${url} (${offer.length} bytes of SDP)`);
		const result = await postOffer({ url, sdp: offer, headers: this.#config.headers, fetchImpl: this.#config.fetchImpl });

		this.#resourceUrl = this.#resourceUrlFrom(result.location, url);
		this.#debugHlsStreamId = result.debugHlsStreamId;
		logger.debug(`[whip] 201, resource=${this.#resourceUrl ?? '<none>'} hlsStreamId=${result.debugHlsStreamId ?? '<none>'}`);
		for (const line of videoSectionLines(result.answerSdp)) logger.debug(`[whip] answer ${line}`);

		await this.#applyAnswer(pc, result.answerSdp);
		await this.#applyDegradationPreference(videoTransceiver.sender);
	}

	/** The peer connection's stats, for whatever the caller wants to watch. `null` once there is nothing to read. */
	async getStats(): Promise<RTCStatsReport | null> {
		if (!this.#pc || this.#closed || this.#pc.connectionState === 'closed') return null;
		return this.#pc.getStats();
	}

	/**
	 * Deletes the session resource and closes the peer connection. Idempotent, and safe on a session that never started.
	 *
	 * The stream and its tracks are left running: they came from the caller, so stopping them — and with them the capture
	 * light — is the caller's call.
	 */
	async close(): Promise<void> {
		if (this.#closed) return;
		this.#closed = true;

		const { logger } = this.#config;
		const resourceUrl = this.#resourceUrl;
		this.#resourceUrl = null;

		if (resourceUrl) {
			const outcome = await deleteResource(resourceUrl, this.#config.fetchImpl);
			// 404 and 410 both mean the resource is already gone, which is the goal.
			if (outcome.ok) logger.debug(`[whip] DELETE ${resourceUrl} -> ${outcome.status}`);
			else logger.warn(`[whip] DELETE ${resourceUrl} failed: ${'error' in outcome ? outcome.error : outcome.status}`);
		}

		this.#pc?.close();
		this.#pc = null;
		this.#emit('statechange', { state: 'closed' });
		this.#listeners.clear();
	}

	/**
	 * The url `close()` should DELETE, or `null` if the server did not give a usable one.
	 *
	 * Either way the stream runs and neither case is the caller's to fix — they are the server not holding up its end,
	 * and they cost the same thing: nothing can delete this session, so it holds a transcoder slot until the server times
	 * it out. `resourceUrl` staying `null` already carries that, which is why a relative `Location` needs no flag of its
	 * own; both values go in the warning so whoever reads the log can see what arrived.
	 */
	#resourceUrlFrom(location: string | null, requestUrl: URL): string | null {
		const { logger } = this.#config;

		if (!location) {
			logger.warn('[whip] no Location header on the 201; this session cannot be deleted');
			return null;
		}

		// A relative Location means the gateway could not resolve its own host. Resolving it against the request url
		// would name whichever host we talked to, and that is the load balancer, which routes only session init — so the
		// result would be a plausible url that always 404s, and a DELETE against it would look like a real attempt.
		if (!/^https?:\/\//i.test(location)) {
			logger.warn(`[whip] Location "${location}" is relative (requested ${requestUrl}); the gateway is misconfigured and this session cannot be deleted`);
			return null;
		}

		return location;
	}

	#requireVideoAndAudioTracks(): [MediaStreamTrack, MediaStreamTrack] {
		const video = this.#config.stream.getVideoTracks();
		const audio = this.#config.stream.getAudioTracks();
		if (video.length !== 1 || audio.length !== 1) {
			throw new WhipError(
				'invalid-options',
				`the ingest endpoint requires exactly 1 video and 1 audio track; this stream has ${video.length} video and ${audio.length} audio`,
			);
		}
		return [video[0]!, audio[0]!];
	}

	/**
	 * The largest frame short side to declare, for the transcoder's ABR ladder.
	 *
	 * The transcoder locks that ladder on the first keyframe, before WebRTC has ramped resolution up, so it has to be
	 * told rather than left to infer. A configured value is a cap, so the smaller of it and what the track is actually
	 * producing is the honest answer; `null` says to send nothing and let the transcoder use its default.
	 */
	#maxVideoShortSide(videoTrack: MediaStreamTrack): number | null {
		const configured = this.#config.maxVideoShortSide;
		if (configured === null) return null;

		const actual = videoShortSide(videoTrack);
		if (!actual && configured === undefined) {
			this.#config.logger.debug('[whip] outgoing video reports no size; the transcoder will use its default ladder');
			return null;
		}

		const declared = actual && configured !== undefined ? Math.min(configured, actual) : (actual ?? configured!);

		// The transcoder rejects an out-of-range value with a 400 rather than capping it silently, so a small capture
		// would otherwise take the whole session down with an error that reads like a malformed offer. Clamping here
		// would defeat the point of that choice, so this fails for the same reason, only sooner and by name.
		if (declared < MIN_VIDEO_SHORT_SIDE || declared > MAX_VIDEO_SHORT_SIDE) {
			throw new WhipError(
				'invalid-options',
				`the ingest endpoint accepts max_video_short_side in ${MIN_VIDEO_SHORT_SIDE}..=${MAX_VIDEO_SHORT_SIDE}, but this stream declares ${declared}`,
			);
		}

		return declared;
	}

	async #createOffer(pc: RTCPeerConnection): Promise<string> {
		try {
			// Argument-less: the browser creates the offer and applies it in one step. Note that this does not wait for ICE
			// gathering — the ingest server has a public IP and publishes a single host candidate, so there is nothing to
			// trickle and nothing to wait for.
			await pc.setLocalDescription();
		} catch (error) {
			throw new WhipError('offer-failed', `could not create the SDP offer: ${errorMessage(error)}`, { cause: error });
		}

		const sdp = pc.localDescription?.sdp;
		if (!sdp) throw new WhipError('offer-failed', 'the peer connection produced no local description');
		return sdp;
	}

	async #applyAnswer(pc: RTCPeerConnection, answerSdp: string): Promise<void> {
		try {
			await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
		} catch (error) {
			// The server resource already exists at this point; `start()` is what gives it back.
			throw new WhipError('answer-failed', `the ingest endpoint's answer was rejected locally: ${errorMessage(error)}`, { cause: error });
		}
	}

	async #applyDegradationPreference(sender: RTCRtpSender): Promise<void> {
		const preference = this.#config.degradationPreference;
		if (!preference) return;

		// Has to run after negotiation: setParameters on a sender that has not negotiated is rejected outright.
		try {
			const parameters = sender.getParameters();
			parameters.degradationPreference = preference;
			await sender.setParameters(parameters);
		} catch (error) {
			// Not worth failing a working stream over: the stream is up, it just degrades the other way under pressure.
			this.#config.logger.warn(`[whip] could not set degradationPreference=${preference}: ${errorMessage(error)}`);
		}
	}

	#watch(pc: RTCPeerConnection): void {
		pc.onconnectionstatechange = () => {
			if (this.#closed) return;
			this.#config.logger.debug(`[whip] ${pc.connectionState}`);
			this.#emit('statechange', { state: pc.connectionState });
		};
		pc.oniceconnectionstatechange = () => this.#config.logger.debug(`[whip] ice ${pc.iceConnectionState}`);
	}

	#emit<T extends WhipEventType>(type: T, event: WhipEventMap[T]): void {
		for (const listener of this.#listeners.get(type) ?? []) {
			try {
				(listener as WhipEventListener<T>)(event);
			} catch (error) {
				this.#config.logger.error(`[whip] a ${type} listener threw: ${errorMessage(error)}`);
			}
		}
	}
}
